/**
 * Pre-Anesthesia Checkup (PAC) Queue & Clinical Clearance Workbench.
 *
 * Designed for anesthetists in the IPD Doctor Console to conduct standard NABH / HMIS
 * pre-operative evaluations. Supports:
 * - Mallampati Airway Classification (Class I - IV)
 * - ASA Physical Status Classification (ASA I - VI + Emergency modifier)
 * - Baseline pre-operative vitals and airway inspection notes
 * - Clearance decisions (FIT advances case to PAC_DONE; OPTIMIZE/UNFIT postpones case)
 * - Review due tracking and suggested optimization orders
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Checkbox,
} from "@mui/material";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { toast } from "react-toastify";

import { fetchPacQueue, recordPac, fetchPac } from "../../../ot/api/otApi";
import { formatIstDateTime, istDayKey } from "../../../../utils/istDateTime";
import { isReviewDue } from "../../../ot/utils/pacQueue";
import PreAnesthesiaAssessmentModal from "../../../ot/components/PreAnesthesiaAssessmentModal";

const ASA_OPTIONS = [
  { value: "I", label: "ASA I — Normal healthy patient" },
  { value: "II", label: "ASA II — Mild systemic disease" },
  { value: "III", label: "ASA III — Severe systemic disease" },
  { value: "IV", label: "ASA IV — Severe systemic disease, constant threat to life" },
  { value: "V", label: "ASA V — Moribund, not expected to survive without surgery" },
  { value: "VI", label: "ASA VI — Declared brain-dead organ donor" },
];

const MALLAMPATI_OPTIONS = [
  { value: "Class I", label: "Class I — Full soft palate, fauces, uvula, pillars visible" },
  { value: "Class II", label: "Class II — Soft palate, fauces, portion of uvula visible" },
  { value: "Class III", label: "Class III — Soft palate and base of uvula visible" },
  { value: "Class IV", label: "Class IV — Hard palate only visible" },
];

/**
 * Pre-Anesthesia Assessment & Clearance Modal (Delegates to PreAnesthesiaAssessmentModal)
 */
function PacClearanceModal({ otCase, onClose, onSaved }) {
  return <PreAnesthesiaAssessmentModal otCase={otCase} onClose={onClose} onSaved={onSaved} />;
}

/**
 * Anesthetist PAC Queue workbench component.
 *
 * Renders tabs for personal assignments, unassigned facility cases, and complete department list,
 * with real-time review-due indicators and clearance dialog trigger.
 */
export default function PacQueuePanel() {
  const [tab, setTab] = useState("mine"); // "mine" | "unassigned" | "all"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "mine") params.mine = true;
      if (tab === "unassigned") params.unassigned = true;
      const res = await fetchPacQueue(params);
      setRows(res?.data || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load PAC Queue");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((r) => {
      const name = (r.patient_name || "").toLowerCase();
      const uhid = (r.health_record_id || r.patient_id || "").toLowerCase();
      const proc = (r.procedure_name || "").toLowerCase();
      const surg = (r.primary_surgeon_name || "").toLowerCase();
      const cnum = (r.case_number || "").toLowerCase();
      return (
        name.includes(q) ||
        uhid.includes(q) ||
        proc.includes(q) ||
        surg.includes(q) ||
        cnum.includes(q)
      );
    });
  }, [rows, searchQuery]);

  return (
    <Box sx={{ p: 2.5, width: "100%", height: "100%", boxSizing: "border-box", overflow: "auto" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
            <HealthAndSafetyIcon sx={{ color: "#0284c7" }} />
            Pre-Anaesthesia Checkup (PAC) Clearance Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Anaesthetist workbench for preoperative airway, ASA stratification, and clinical optimization review.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Search patient, UHID, procedure…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ color: "text.disabled", mr: 0.5 }} />,
            }}
            sx={{ width: 280, bgcolor: "#fff", borderRadius: 1 }}
          />
          <Tooltip title="Refresh queue">
            <IconButton onClick={load} disabled={loading} sx={{ bgcolor: "#fff", border: "1px solid #cbd5e1" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          sx={{
            borderBottom: "1px solid #e2e8f0",
            bgcolor: "#fff",
            px: 2,
            "& .MuiTab-root": { fontWeight: 700, fontSize: "0.8rem", textTransform: "none", minHeight: 48 },
          }}
        >
          <Tab value="mine" label="My Assigned Cases" />
          <Tab value="unassigned" label="Unassigned Cases" />
          <Tab value="all" label="All Pending PAC Queue" />
        </Tabs>

        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={32} />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
            <HealthAndSafetyIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
            <Typography variant="subtitle2">No cases awaiting PAC clearance in this view.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569" }}>Case # / Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569" }}>Patient Details</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569" }}>Procedure</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569" }}>Surgeon</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569" }}>Anaesthetist</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569" }}>Scheduled Surgery</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569" }}>Review / Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#475569", textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => {
                  const reviewDue = isReviewDue(row, istDayKey(new Date()));
                  const isEmergency = (row.scheduling_type || "").toUpperCase() === "EMERGENCY";

                  return (
                    <TableRow key={row.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                          #{row.case_number || row.id}
                        </Typography>
                        {isEmergency ? (
                          <Chip size="small" label="EMERGENCY" color="error" sx={{ height: 20, fontSize: 10, fontWeight: 800 }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {row.scheduling_type || "ELECTIVE"}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                          {row.patient_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.health_record_id || row.patient_id} · {row.gender} {row.age ? `${row.age}y` : ""}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
                          {row.procedure_name || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {row.primary_surgeon_name || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {row.anaesthetist_name ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#0284c7" }}>
                            {row.anaesthetist_name}
                          </Typography>
                        ) : (
                          <Chip size="small" variant="outlined" label="Unassigned" sx={{ height: 20, fontSize: 10 }} />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatIstDateTime(row.next_booking_start, "Not booked")}
                        </Typography>
                        {row.operating_room_name && (
                          <Typography variant="caption" color="text.secondary">
                            {row.operating_room_name}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {reviewDue ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={`Review Due (${row.next_review_date})`}
                            sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                          />
                        ) : row.lifecycle_status === "POSTPONED" ? (
                          <Chip size="small" color="error" variant="outlined" label="Postponed" sx={{ height: 22, fontSize: 11 }} />
                        ) : (
                          <Chip size="small" color="default" label="Clearance Pending" sx={{ height: 22, fontSize: 11 }} />
                        )}
                      </TableCell>

                      <TableCell sx={{ textAlign: "right" }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setSelectedCase(row)}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            px: 1.5,
                            bgcolor: "#0284c7",
                            "&:hover": { bgcolor: "#0369a1" },
                          }}
                        >
                          Clearance Exam
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {selectedCase && (
        <PacClearanceModal
          otCase={selectedCase}
          onClose={() => setSelectedCase(null)}
          onSaved={load}
        />
      )}
    </Box>
  );
}
